import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { parseLocale } from './useLocale';
import { COOKIES, getCookie } from 'utils/helpers';
import type { TCountry } from 'actions/global';
import { BRITISH_COUNTRIES } from 'utils/constants/i18n';

const ignoredRoutes = ['bolsa', 'compra', 'pedido'];

export function useGeoDetection(countries: TCountry[]) {
  const router = useRouter();
  const { country } = parseLocale(router.locale);
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const [alreadyDetected, setAlreadyDetected] = useState<boolean>(false);

  const { detectedCountryData, defaultCountryData } = useMemo(() => {
    return {
      detectedCountryData: countries.filter((item) => item.id === detectedCountry)[0],
      defaultCountryData: countries.filter((item) => item.id === country)[0],
    };
  }, [countries, detectedCountry, country]);

  useEffect(() => {
    const detectedCountry = getCookie(COOKIES.DETECTED_COUNTRY);
    const alreadyDetected = getCookie(COOKIES.ALREADY_DETECTED);
    if (detectedCountry && /^[A-Z]{2}$/.test(detectedCountry)) {
      setDetectedCountry(detectedCountry);
    }
    if (alreadyDetected) {
      setAlreadyDetected(true);
    }
  }, [country]);

  const showGeoModal = useMemo(() => {
    return (
      !alreadyDetected &&
      !ignoredRoutes.some((r) => router.asPath.startsWith(`/${r}`)) &&
      detectedCountry !== null &&
      detectedCountry !== country &&
      !BRITISH_COUNTRIES.includes(country) &&
      detectedCountryData !== undefined &&
      defaultCountryData !== undefined
    );
  }, [detectedCountry, country, detectedCountryData, defaultCountryData, alreadyDetected, router.asPath]);

  return { detectedCountry, showGeoModal };
}
